import React, { useState, useEffect } from 'react'
import { Box, Flex, Select, Group, Button, TextInput, Loader, Text, Tabs, Avatar, SimpleGrid, Divider } from '@mantine/core'
import { useDebouncedCallback } from '@mantine/hooks'
import { getAllCountryList } from '../../../services/location'
import LoadingData from '../../ui/LoadingData'
import { IconCalendar, IconCheck, IconClipboardText, IconFileText, IconMapPin, IconUser } from '@tabler/icons-react'
import { getListUsers } from '../../../services/users'
import { DateInput } from '@mantine/dates'
import { getAllStatusesList } from '../../../services/status'

const defaultCondition = {
  salesDivision: [{ id: 9 }, { id: 37 }, { id: 39 }, { id: 44 }, { id: 45 }, { id: 54 }, { id: 55 }, { id: 68 }, { id: 78 }],
  purchaseDivision: [{ id: 10 }, { id: 46 }, { id: 81 }],
  logisticDivision: [{ id: 11 }, { id: 12 }, { id: 47 }],
  aftersalesDivision: [{ id: 13 }, { id: 40 }, { id: 79 }, { id: 80 }],
}

const defaultParams = {
  'page[num]': 1,
  'page[limit]': 10,
  'filter[order_code][like]': null,
  'filter[marking_code][like]': null,
  'filter[order_infos][like]': null,
  'filter[user_id][is]': null,
  'filter[agent.name][like]': null,
  'filter[sales.name][like]': null,
  'filter[shipment_from][is]': null,
  'filter[shipment_to][is]': null,
  'filter[order_date][like]': null,
  'filter[order_date][gte]': null,
  'filter[order_date][lte]': null,
  'filter[created_at][like]': null,
  'filter[created_at][gte]': null,
  'filter[created_at][lte]': null,
  'filter[statuses.id][is]': null,
}


const FormFilterOrders = ({ paramsOrder, applyFilter, onClose }) => {
  const [parameter, setParameter] = useState(defaultParams)

  const [countryList, setCountryList] = useState([])
  const [loadingCountry, setLoadingCountry] = useState(true)

  // users
  const [searchUsers, setSearchUsers] = useState('')
  const [userList, setUserList] = useState([])
  const [loadingUsers, setLoadingUsers] = useState(false)
  // agents
  const [searchUserAgents, setSearchUserAgents] = useState('')
  const [userAgentList, setUserAgentList] = useState([])
  const [loadingUserAgents, setLoadingUserAgents] = useState(false)
  // sales
  const [searchUserSales, setSearchUserSales] = useState('')
  const [userSalesList, setUserSalesList] = useState([])
  const [loadingUserSales, setLoadingUserSales] = useState(false)

  // order status
  const [orderStatusList, setOrderStatusList] = useState([])

  const handleGetListCountry = useDebouncedCallback(async () => {
    setLoadingCountry(true)
    const parameter = {
      'page[num]': 1,
      'page[limit]': 9999,
      'fields[country]': 'name',
    }
    try {
      const response = await getAllCountryList(parameter)
      const dataCountry = response.data
      const remapDataCountry = dataCountry.map((val) => {
        return {
          value: `${val.attributes.id}`,
          label: val.attributes.name
        }
      })
      setCountryList(remapDataCountry)
    } catch (error) {
      console.log(error)
    } finally {
      setLoadingCountry(false)
    }
  }, 300)

  const handleGetUsers = useDebouncedCallback(async (id) => {
    setLoadingUsers(true)
    const parameter = {
      'filter[id][is]': id,
      'filter[name][like]': searchUsers,
      'page[num]': 1,
      'page[limit]': 10,
    }
    try {
      const response = await getListUsers(parameter)
      const { data } = response.data
      const remap = data.map((val) => {
        return {
          value: `${val.attributes.id}`,
          label: val.attributes.name,
          email: val.attributes.email,
        }
      })
      setUserList(remap)
    } catch (error) {
      console.log(error)
    } finally {
      setLoadingUsers(false)
    }
  }, 300)

  const handleGetUserAgents = useDebouncedCallback(async (id) => {
    setLoadingUserAgents(true)
    const parameter = {
      'filter[id][is]': id,
      'filter[name][like]': searchUserAgents,
      'page[num]': 1,
      'page[limit]': 10,
      'filter[is_agent][is]': 1,
    }
    try {
      const response = await getListUsers(parameter)
      const { data } = response.data
      const remap = data.map((val) => {
        return {
          value: val.attributes.name,
          label: val.attributes.name,
          email: val.attributes.email,
        }
      })
      setUserAgentList(remap)
    } catch (error) {
      console.log(error)
    } finally {
      setLoadingUserAgents(false)
    }
  }, 300)

  const handleGetUserSales = useDebouncedCallback(async (id) => {
    setLoadingUserSales(true)
    const parameter = {
      'filter[id][is]': id,
      'filter[name][like]': searchUserSales,
      'page[num]': 1,
      'page[limit]': 10,
    }
    try {
      const response = await getListUsers(parameter)
      const { data } = response.data
      const remap = data.map((val) => {
        return {
          value: val.attributes.name,
          label: val.attributes.name,
          email: val.attributes.email,
        }
      })
      setUserSalesList(remap)
    } catch (error) {
      console.log(error)
    } finally {
      setLoadingUserSales(false)
    }
  }, 300)

  const handleGetOrderStatus = async () => {
    const parameter = {
      'filter[status_type][is]': 2,
    }
    try {
      const response = await getAllStatusesList(parameter)
      const dataStatus = response.data

      const handleFilterStatus = (data, division) => {
        // eslint-disable-next-line
        const filterData = data.filter((val) => {
          let data = division
          if (data.length > 0) {
            return data.find(value => value.id === val.attributes.id)
          }
        })
        return filterData.map(e => {
          return {
            value: `${e.attributes.id}`,
            label: e.attributes.name,
          }
        })
      }

      const divisionSales = handleFilterStatus(dataStatus, defaultCondition.salesDivision)
      const divisionPurchase = handleFilterStatus(dataStatus, defaultCondition.purchaseDivision)
      const divisionLogistic = handleFilterStatus(dataStatus, defaultCondition.logisticDivision)
      const divisionAftersales = handleFilterStatus(dataStatus, defaultCondition.aftersalesDivision)

      const orderStatusByDivision = [
        {
          group: 'Sales Division',
          items: divisionSales,
        },
        {
          group: 'Purchase & Warehouse Division',
          items: divisionPurchase,
        },
        {
          group: 'Logistic Division',
          items: divisionLogistic,
        },
        {
          group: 'Aftersales Division',
          items: divisionAftersales,
        },
      ]
      setOrderStatusList(orderStatusByDivision)
    } catch (error) {
      console.log(error)
    }
  }

  useEffect(() => {
    handleGetListCountry()
    handleGetOrderStatus()
    if (paramsOrder !== null) {
      handleSetParams(paramsOrder)
    }
    // eslint-disable-next-line
  }, [paramsOrder])

  useEffect(() => {
    handleGetUsers()
    // eslint-disable-next-line
  }, [searchUsers])
  useEffect(() => {
    handleGetUserAgents()
    // eslint-disable-next-line
  }, [searchUserAgents])
  useEffect(() => {
    handleGetUserSales()
    // eslint-disable-next-line
  }, [searchUserSales])

  const handleSetParams = (val) => {
    const prevParams = {
      'filter[order_code][like]': val['filter[order_code][like]'],
      'filter[marking_code][like]': val['filter[marking_code][like]'],
      'filter[order_infos][like]': val['filter[order_infos][like]'],
      'filter[user_id][is]': val['filter[user_id][is]'],
      'filter[agent.name][like]': val['filter[agent.name][like]'],
      'filter[sales.name][like]': val['filter[sales.name][like]'],
      'filter[shipment_from][is]': val['filter[shipment_from][is]'],
      'filter[shipment_to][is]': val['filter[shipment_to][is]'],
      'filter[order_date][like]': val['filter[order_date][like]'],
      'filter[order_date][gte]': val['filter[order_date][gte]'],
      'filter[order_date][lte]': val['filter[order_date][lte]'],
      'filter[created_at][like]': val['filter[created_at][like]'],
      'filter[created_at][gte]': val['filter[created_at][gte]'],
      'filter[created_at][lte]': val['filter[created_at][lte]'],
      'filter[statuses.id][is]': val['filter[statuses.id][is]'],
    }
    setParameter(prevParams)
    setSearchUserAgents(val['filter[agent.name][like]'] || '')
    setSearchUserSales(val['filter[sales.name][like]'] || '')
  }

  const handleChangeFilter = (name, val) => {
    setParameter((oldVal) => ({ ...oldVal, [name]: val }))
  }

  const handleApplyFilter = (params) => {
    const valueParams = {
      ...params,
      'page[num]': 1,
      'page[limit]': 10,
    }

    applyFilter(valueParams)
    onClose()
  }

  const renderUserOption = ({ option, checked }) => {
    return (
      <Group flex="1" align='center' gap="xs">
        <Flex gap='sm' align='center'>
          <Avatar variant='filled' name={option.label} radius="xl" size="sm" color='red' />
          <Box>
            <Text fz={12} fw='600' lineClamp={2}>{option.label}</Text>
            <Text fz={10} fw='500' c='gray.6' lineClamp={2}>{option.email}</Text>
          </Box>
        </Flex>
        {checked && <IconCheck style={{ marginInlineStart: 'auto' }} />}
      </Group>
    )
  }

  const loadingDataTemplate = () => {
    return (
      <Box ta='center' my={12}>
        <Loader size={16} />
        <Text c='gray.6' fz={11}>Loading Data</Text>
      </Box>
    )
  }

  const renderTabMenus = () => {
    return (
      <Tabs.List style={{ marginRight: '16px' }}>
        <Tabs.Tab value="order-number" leftSection={<IconClipboardText size={12} />}>
          Order Number
        </Tabs.Tab>
        <Divider my={4} />
        <Tabs.Tab value="marking-code" leftSection={<IconClipboardText size={12} />}>
          Batch/Marking Code
        </Tabs.Tab>
        <Divider my={4} />
        <Tabs.Tab value="created-by-user" leftSection={<IconUser size={12} />}>
          Created by User
        </Tabs.Tab>
        <Divider my={4} />
        <Tabs.Tab value="assisted-by-agent" leftSection={<IconUser size={12} />}>
          Assisted by Agent
        </Tabs.Tab>
        <Divider my={4} />
        <Tabs.Tab value="handle-by-sales" leftSection={<IconUser size={12} />}>
          Handle by Sales
        </Tabs.Tab>
        <Divider my={4} />
        <Tabs.Tab value="customer-name" leftSection={<IconUser size={12} />}>
          Customer Name
        </Tabs.Tab>
        <Divider my={4} />
        <Tabs.Tab value="order-status" leftSection={<IconFileText size={12} />}>
          Order Status
        </Tabs.Tab>
        <Divider my={4} />
        <Tabs.Tab value="shipment-from" leftSection={<IconMapPin size={12} />}>
          Shipment From
        </Tabs.Tab>
        <Divider my={4} />
        <Tabs.Tab value="shipment-to" leftSection={<IconMapPin size={12} />}>
          Shipment To
        </Tabs.Tab>
        <Divider my={4} />
        <Tabs.Tab value="order-date" leftSection={<IconCalendar size={12} />}>
          Order Date
        </Tabs.Tab>
        <Divider my={4} />
        <Tabs.Tab value="created-date" leftSection={<IconCalendar size={12} />}>
          Careated Date
        </Tabs.Tab>
      </Tabs.List>
    )
  }

  const renderTabsPanel = () => {
    return (
      <>
        <Tabs.Panel value="order-number">
          <TextInput
            name='filter[order_code][like]'
            value={parameter['filter[order_code][like]']}
            onChange={(val) => handleChangeFilter('filter[order_code][like]', val.target.value)}
            size='xs'
            label="Order Code"
          />
        </Tabs.Panel>

        <Tabs.Panel value="marking-code">
          <TextInput
            name='filter[marking_code][like]'
            value={parameter['filter[marking_code][like]']}
            onChange={(val) => handleChangeFilter('filter[marking_code][like]', val.target.value)}
            size='xs'
            label="Marking Code"
          />
        </Tabs.Panel>

        <Tabs.Panel value="created-by-user">
          <Select
            name='filter[user_id][is]'
            value={parameter['filter[user_id][is]']}
            label='Ordered by User'
            placeholder="Choose User"
            data={userList}
            renderOption={renderUserOption}
            onChange={(val) => handleChangeFilter('filter[user_id][is]', val)}
            size='xs'
            searchable
            searchValue={searchUsers}
            onSearchChange={setSearchUsers}
            nothingFoundMessage={!loadingUsers ? userList.length > 0 ? '' : 'No User Found' : loadingDataTemplate()}
            clearable
          />
        </Tabs.Panel>

        <Tabs.Panel value="assisted-by-agent">
          <Select
            name='filter[agent.name][like]'
            value={parameter['filter[agent.name][like]']}
            label='Agent'
            placeholder="Choose User"
            data={userAgentList}
            renderOption={renderUserOption}
            onChange={(val) => handleChangeFilter('filter[agent.name][like]', val)}
            size='xs'
            searchable
            searchValue={searchUserAgents}
            onSearchChange={setSearchUserAgents}
            nothingFoundMessage={!loadingUserAgents ? userAgentList.length > 0 ? '' : 'No User Found' : loadingDataTemplate()}
            clearable
          />
        </Tabs.Panel>

        <Tabs.Panel value="handle-by-sales">
          <Select
            name='filter[sales.name][like]'
            value={parameter['filter[sales.name][like]']}
            label='Sales'
            placeholder="Choose User"
            data={userSalesList}
            renderOption={renderUserOption}
            onChange={(val) => handleChangeFilter('filter[sales.name][like]', val)}
            size='xs'
            searchable
            searchValue={searchUserSales}
            onSearchChange={setSearchUserSales}
            nothingFoundMessage={!loadingUserSales ? userSalesList.length > 0 ? '' : 'No User Found' : loadingDataTemplate()}
            clearable
          />
        </Tabs.Panel>

        <Tabs.Panel value="order-status">
          <Select
            label="Order Status"
            data={orderStatusList}
            name='filter[statuses.id][is]'
            value={parameter['filter[statuses.id][is]']}
            onChange={(val) => handleChangeFilter('filter[statuses.id][is]', val)}
            size='xs'
            searchable
            clearable
          />
        </Tabs.Panel>

        <Tabs.Panel value="shipment-from">
          <Select
            name='filter[shipment_from][is]'
            value={parameter['filter[shipment_from][is]']}
            label='Shipment From'
            data={countryList}
            onChange={(val) => handleChangeFilter('filter[shipment_from][is]', val)}
            size='xs'
            clearable
            searchable
          />
        </Tabs.Panel>

        <Tabs.Panel value="shipment-to">
          <Select
            name='filter[shipment_to][is]'
            value={parameter['filter[shipment_to][is]']}
            label='Shipment To'
            data={countryList}
            onChange={(val) => handleChangeFilter('filter[shipment_to][is]', val)}
            size='xs'
            clearable
            searchable
          />
        </Tabs.Panel>

        <Tabs.Panel value="customer-name">
          <TextInput
            name='filter[order_infos][like]'
            value={parameter['filter[order_infos][like]']}
            onChange={(val) => handleChangeFilter('filter[order_infos][like]', val.target.value)}
            size='xs'
            label="Costumer Name"
          />
        </Tabs.Panel>

        <Tabs.Panel value="order-date">
          <Text size="lg" c="dimmed">Range Order Date</Text>
          <SimpleGrid cols={2}>
            <DateInput
              valueFormat="DD/MM/YYYY"
              value={parameter['filter[order_date][like]']}
              onChange={(val) => handleChangeFilter('filter[order_date][like]', val)}
              label="Order Date"
              placeholder="Date input"
              clearable
              size='xs'
            />
            <Box />

            <DateInput
              valueFormat="DD/MM/YYYY"
              value={parameter['filter[order_date][gte]']}
              onChange={(val) => handleChangeFilter('filter[order_date][gte]', val)}
              label="From Date"
              placeholder="Date input"
              clearable
              size='xs'
            />

            <DateInput
              valueFormat="DD/MM/YYYY"
              value={parameter['filter[order_date][lte]']}
              onChange={(val) => handleChangeFilter('filter[order_date][lte]', val)}
              label="To Date"
              placeholder="Date input"
              clearable
              size='xs'
            />
          </SimpleGrid>
        </Tabs.Panel>

        <Tabs.Panel value="created-date">
          <Text size="lg" c="dimmed">Range Order Date</Text>
          <SimpleGrid cols={2}>
            <DateInput
              valueFormat="DD/MM/YYYY"
              value={parameter['filter[created_at][like]']}
              onChange={(val) => handleChangeFilter('filter[created_at][like]', val)}
              label="Created Date"
              placeholder="Date input"
              clearable
              size='xs'
            />
            <Box />

            <DateInput
              valueFormat="DD/MM/YYYY"
              value={parameter['filter[created_at][gte]']}
              onChange={(val) => handleChangeFilter('filter[created_at][gte]', val)}
              label="From Date"
              placeholder="Date input"
              clearable
              size='xs'
            />

            <DateInput
              valueFormat="DD/MM/YYYY"
              value={parameter['filter[created_at][lte]']}
              onChange={(val) => handleChangeFilter('filter[created_at][lte]', val)}
              label="To Date"
              placeholder="Date input"
              clearable
              size='xs'
            />
          </SimpleGrid>
        </Tabs.Panel>
      </>
    )
  }

  const mappingFormFilter = () => {
    return (
      <Box>
        <Divider mb={10} />
        <Tabs orientation="vertical" defaultValue="order-number">
          {renderTabMenus()}
          {renderTabsPanel()}
        </Tabs>
        <Divider mt={10} />
        <Box mt={20}>
          <Flex justify='flex-end'>
            <Group>
              <Button size='xs' variant="outline" onClick={onClose}>Close</Button>
              <Button size='xs' variant="filled" onClick={() => handleApplyFilter(parameter)}>Apply Filter</Button>
            </Group>
          </Flex>
        </Box>
      </Box>
    )
  }

  return (
    <Box>
      {loadingCountry ? <LoadingData /> : mappingFormFilter()}
    </Box>
  )
}

export default FormFilterOrders